import type { RootStore } from '@/stores/root-store.types';
import type { StoreApi } from 'zustand';
import { InboxItem, MessageItem } from '@/services/chat';
import {
  mapChatResponseToUIProps,
  mapNewChatFromMessage
} from '@/utils/chat-mappers/chat.mapper';
import { mapMessagesResponseToUIProps } from '@/utils/chat-mappers/messages.mapper';
import { MessageSentEvent } from './chat.types';
import { NewMessageEvent } from '@/hooks/useSocket';

export const createChatSlice = (
  set: StoreApi<RootStore>['setState'],
  get: StoreApi<RootStore>['getState'],
  ...rest: StoreApi<RootStore>[]
) => ({
  isConnected: false,
  events: [],
  chats: [],
  selectedChatId: null,
  setIsConnected: (value: boolean) => {
    set({
      isConnected: value
    });
  },
  setEvent: (event: string) => {
    set({
      events: [...get().events, event]
    });
  },
  setSelectedChatId: (id: string) => {
    set({
      selectedChatId: id
    });
  },
  clearEvents: () => {
    set({
      events: []
    });
  },
  setChats: (chats: InboxItem[]) => {
    const existingChats = get().chats;
    const parsedChats = chats.map((chat) =>
      mapChatResponseToUIProps(chat, existingChats)
    );
    set({
      chats: parsedChats
    });
  },
  setMessagesOnOpenChat: (messages: MessageItem[], id: string) => {
    const selectedChat = get().chats.find((chat) => chat.id === id);
    if (selectedChat) {
      const parsedMessages = mapMessagesResponseToUIProps(
        messages,
        selectedChat
      );
      set({
        chats: get().chats.map((chat) =>
          chat.id === id
            ? {
                ...chat,
                unread: 0,
                messages: parsedMessages
              }
            : chat
        )
      });
    }
  },
  pushMessageSent: (data: MessageSentEvent, position: 'left' | 'right') => {
    const selectedChat = get().chats.find(
      (chat) => chat.id === data.originalPayload.contactId
    );
    if (selectedChat) {
      const isMessageAlreadyPushed = selectedChat.messages?.some(
        (message) => message.id === data.messageId
      );
      if (!isMessageAlreadyPushed) {
        const newMessage = {
          id: data.messageId,
          position,
          date: new Date(data.timestamp),
          status: 1, // ?
          title: 'You',
          type: 'text',
          text: data.originalPayload.message,
          reply: undefined
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        selectedChat.messages.push(newMessage); // TODO: push instead set?
      }
    }
  },
  pushNewMessage: (data: NewMessageEvent) => {
    const chats = get().chats;
    const selectedChat = chats.find((chat) => chat.id === data.contactId);
    if (selectedChat) {
      const isMessageAlreadyPushed = selectedChat.messages?.some(
        (message) => message.id === data.message.id
      );
      if (!isMessageAlreadyPushed) {
        const newMessage = {
          id: data.messageDetails?.id,
          position: data.sendPulseData?.direction === 1 ? 'left' : 'right',
          date: new Date(data.timestamp),
          status: data.messageDetails?.status || 1, // Assuming 1 is a default status
          title:
            data.messageDetails?.direction === 1 ? data.message.from : 'You',
          type: 'text',
          text: data.message.text,
          reply: undefined
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        selectedChat.messages.push(newMessage); // TODO: push instead set?

        // Update the chat state with the new message (if from the user)
        if (data.sendPulseData?.direction === 1) {
          selectedChat.subtitle = data.message.text;
          selectedChat.date = new Date(
            data.messageDetails?.createdAt || data.timestamp
          );
        }
        if (get().selectedChatId !== selectedChat.id) {
          selectedChat.unread = selectedChat.unread + 1;
        }
      }
    } else {
      // If the chat does not exist, we can create a new one
      const newChat = mapNewChatFromMessage(data);
      set({
        chats: [...chats, newChat]
      });
    }
  }
});
